import { STORAGE_KEYS } from '../constants/storageKeys';


// Helper function to check if a distinct_id is anonymous (UUID format)
export const isAnonymousId = (distinctId) => {
  if (!distinctId) return true;
  // PostHog anonymous IDs are UUIDs (e.g., "019ac559-fe55-70e6-9caa-c1a7f884e46d")
  // Email addresses are not UUIDs, so if it matches UUID pattern, it's anonymous
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidPattern.test(distinctId);
};

// Internal helper to perform the actual identification logic
// Note: We use email as the distinct_id in PostHog (PostHog's unique identifier)
const _performIdentifyInternal = (posthogInstance, email, userData) => {
  if (!posthogInstance || !email) {
    return;
  }

  // Identify with all user properties directly (no alias needed)
  // Since we identify immediately, we don't need to merge anonymous history
  // We use email as the distinct_id (PostHog's unique identifier)
  posthogInstance.identify(email, {
    email: userData?.email || email,
    name: userData?.name,
    subscription_type: userData?.subscription_type,
    account_type: userData?.account_type,
    dot_authorized: userData?.dot_authorized,
    has_had_trial: userData?.has_had_trial,
  });

  // Track the last identified user
  localStorage.setItem(STORAGE_KEYS.POSTHOG_LAST_IDENTIFIED_USER, email);

  // Opt back in to capturing now that user is identified
  posthogInstance.opt_in_capturing();
  
  // Verify opt-in worked (for debugging)
  if (process.env.NODE_ENV !== 'production') {
    const isOptedIn = !posthogInstance.has_opted_out_capturing();
    console.log('PostHog opt-in status:', isOptedIn ? 'opted in' : 'still opted out');
  }

  // Now capture the pageview after identification
  posthogInstance.capture('$pageview');

  // Start session recording if enabled
  if (process.env.REACT_APP_POSTHOG_RECORDING === 'true') {
    // Check if recording already started to avoid duplicate starts
    if (!posthogInstance.sessionRecordingStarted) {
      posthogInstance.startSessionRecording(true);
    }
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log('PostHog: User identified:', email);
  }
};

// Helper function to perform identification and capture (for index.js usage)
// Signature: (posthog, email, data)
export const performIdentifyWithCapture = (posthog, email, data) => {
  _performIdentifyInternal(posthog, email, data);
};

// Helper function to perform identification (for AuthContext usage)
// Signature: (email, userData) - email is used as PostHog's distinct_id
export const performIdentify = (email, userData) => {
  if (!window.posthog) {
    return;
  }
  _performIdentifyInternal(window.posthog, email, userData);
};

// Helper function to identify and capture pageview
export const identifyAndCapture = (posthog, email, data) => {
  // Check if PostHog has already created an anonymous person profile
  // If so, reset it first so the person profile is created fresh with email as primary ID
  const currentDistinctId = posthog.get_distinct_id();
  if (isAnonymousId(currentDistinctId)) {
    // PostHog has an anonymous profile - reset it so we create a fresh one with email
    posthog.reset();
    // Small delay to ensure reset completes
    setTimeout(() => {
      performIdentifyWithCapture(posthog, email, data);
    }, 50);
  } else {
    // No anonymous profile or already identified - proceed normally
    performIdentifyWithCapture(posthog, email, data);
  }
};

// Get user data from localStorage
export const getUserFromStorage = () => {
  try {
    const storedUser = localStorage.getItem("user");
    const userData = storedUser ? JSON.parse(storedUser) : null;
    return {
      userData,
      userEmail: userData?.email,
    };
  } catch (error) {
    console.error('Error reading user from localStorage:', error);
    return {
      userData: null,
      userEmail: null,
    };
  }
};

// Handle PostHog loaded callback - identifies user if logged in
export const handlePostHogLoaded = (posthog, userEmail, userData) => {
  // If no user is logged in, opt out of capturing until they log in
  // This prevents anonymous events from being sent
  if (!userEmail || !userData) {
    posthog.opt_out_capturing();
    if (process.env.NODE_ENV !== 'production') {
      console.log('PostHog: Opted out of capturing - waiting for user identification');
    }
    return;
  }

  // PostHog is now loaded and ready
  // Identify user immediately if they're already logged in (from localStorage)
  // This ensures all events (including the initial pageview) are associated with the user's email
  try {
    // Check if this is a different user than the one currently identified
    const lastIdentifiedUser = localStorage.getItem(STORAGE_KEYS.POSTHOG_LAST_IDENTIFIED_USER);
    
    // Get PostHog's current distinct_id to check if it's anonymous
    const currentDistinctId = posthog.get_distinct_id();
    const hasAnonymousProfile = isAnonymousId(currentDistinctId);
    
    // If it's a different user OR PostHog has an anonymous profile, reset first
    if ((lastIdentifiedUser && lastIdentifiedUser !== userEmail) || hasAnonymousProfile) {
      posthog.reset();
      // Wait a moment for reset to complete
      setTimeout(() => {
        identifyAndCapture(posthog, userEmail, userData);
      }, 100);
    } else {
      identifyAndCapture(posthog, userEmail, userData);
    }
  } catch (error) {
    console.error('PostHog identify error on load:', error);
    // Fallback: opt in and capture pageview even if identify fails
    posthog.opt_in_capturing();
    posthog.capture('$pageview');
  }
};

// Helper function to safely identify a user in PostHog
// Used when user logs in or updates their profile
// email is used as PostHog's distinct_id (unique identifier)
export const identifyUserInPostHog = (email, userData) => {
  if (!process.env.REACT_APP_POSTHOG_KEY || !window.posthog || !email) {
    return;
  }

  try {
    // Check if this is a different user than the one currently identified
    const lastIdentifiedUser = localStorage.getItem(STORAGE_KEYS.POSTHOG_LAST_IDENTIFIED_USER);
    
    // Get PostHog's current distinct_id to check if it's anonymous
    const currentDistinctId = window.posthog.get_distinct_id();
    const hasAnonymousProfile = isAnonymousId(currentDistinctId);
    
    // If it's a different user OR PostHog has an anonymous profile, reset first
    if ((lastIdentifiedUser && lastIdentifiedUser !== email) || hasAnonymousProfile) {
      window.posthog.reset();
      // Clear the tracking
      localStorage.removeItem(STORAGE_KEYS.POSTHOG_LAST_IDENTIFIED_USER);
      // Wait a moment for reset to complete before identifying new user
      setTimeout(() => {
        performIdentify(email, userData);
      }, 50);
    } else {
      // Same user and no anonymous profile - just identify directly
      performIdentify(email, userData);
    }
  } catch (error) {
    console.error('PostHog identify error:', error);
  }
};

// Initialize PostHog with configuration
export const initPostHog = (posthogInstance) => {
  // Initialize PostHog only if the key is provided
  if (!process.env.REACT_APP_POSTHOG_KEY) {
    return;
  }

  // Check if user data exists in localStorage to identify immediately
  const { userData, userEmail } = getUserFromStorage();

  posthogInstance.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com',
    capture_pageview: false, // We'll capture pageview manually after identification
    person_profiles: 'always', // Create profiles for all visitors, including anonymous ones
    debug: process.env.NODE_ENV !== 'production',
    loaded: (posthog) => {
      handlePostHogLoaded(posthog, userEmail, userData);
    },
  });

  if (process.env.NODE_ENV !== 'production' && !userEmail) {
    console.log('PostHog loaded - opted out of capturing until user logs in');
  }
};

